import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Client,
  type Conversation,
  type Message,
  type Participant,
} from '@twilio/conversations'
import type {
  MessageType,
  TwilioTokenResponse,
  UseTwilioChatOptions,
  UseTwilioChatReturn,
} from '../types/chat.types'
import {
  sendMessageViaApi,
  getChatHistory,
  type ChatHistoryMessage,
} from '../services/chatApi'

const isBrowser = typeof window !== 'undefined'
const DEFAULT_REFRESH_MS = 1000 * 60 * 20 // 20 minutes
const TYPING_PING_MS = 3500

const sortMessages = (items: MessageType[]) =>
  [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

const getClientIdentity = (twilioClient: Client) => {
  const user =
    (twilioClient as unknown as { user?: { identity?: string } }).user ?? null
  return user?.identity ?? null
}

const mapChatHistoryMessage = (
  historyMsg: ChatHistoryMessage,
  currentUserId: string | null,
  isTherapist: boolean
): MessageType => {
  const isOutbound =
    (isTherapist && historyMsg.senderRole === 'therapist') ||
    (!isTherapist && historyMsg.senderRole === 'patient')

  return {
    sid: historyMsg.twilioMessageSid || historyMsg._id,
    author: historyMsg.senderId,
    body: historyMsg.message,
    createdAt: historyMsg.createdAt,
    direction: isOutbound ? 'outbound' : 'inbound',
    status: 'sent',
  }
}

export const useTwilioChat = (
  options?: UseTwilioChatOptions
): UseTwilioChatReturn => {
  const [client, setClient] = useState<Client | null>(null)
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<MessageType[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [typingParticipant, setTypingParticipant] = useState<string | null>(
    null
  )
  const [, setParticipantVersion] = useState(0)
  const [userIdentity, setUserIdentity] = useState<string | null>(
    options?.identity ?? null
  )

  const clientRef = useRef<Client | null>(null)
  const conversationRef = useRef<Conversation | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const typingTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const fetchOptionsRef = useRef<UseTwilioChatOptions['fetchOptions']>(
    options?.fetchOptions
  )
  const tokenFetcherRef = useRef<UseTwilioChatOptions['tokenFetcher']>(
    options?.tokenFetcher
  )
  const conversationResolverRef = useRef<
    UseTwilioChatOptions['conversationResolver']
  >(options?.conversationResolver)

  const tokenEndpoint =
    options?.tokenEndpoint ?? import.meta.env.VITE_TWILIO_TOKEN_ENDPOINT
  const autoRefreshMs = options?.autoRefreshMs ?? DEFAULT_REFRESH_MS

  useEffect(() => {
    fetchOptionsRef.current = options?.fetchOptions
  }, [options?.fetchOptions])

  useEffect(() => {
    tokenFetcherRef.current = options?.tokenFetcher
  }, [options?.tokenFetcher])

  useEffect(() => {
    conversationResolverRef.current = options?.conversationResolver
  }, [options?.conversationResolver])

  useEffect(() => {
    if (options?.identity && options.identity !== userIdentity) {
      setUserIdentity(options.identity)
    }
  }, [options?.identity, userIdentity])

  const debug = useCallback(
    (...args: unknown[]) => {
      if (options?.enableDebugLogging) {
        // eslint-disable-next-line no-console
        console.debug('[useTwilioChat]', ...args)
      }
    },
    [options?.enableDebugLogging]
  )

  const mapMessage = useCallback(
    (message: Message, identityOverride?: string | null): MessageType => {
      const author = message.author ?? 'Unknown'
      const createdAt =
        message.dateCreated?.toISOString() ?? new Date().toISOString()
      const resolvedIdentity = identityOverride ?? userIdentity ?? undefined

      return {
        sid: message.sid,
        author,
        body: message.body ?? '',
        createdAt,
        participantSid: message.participantSid ?? undefined,
        status: (message as Message & { status?: string }).status,
        direction:
          resolvedIdentity && author === resolvedIdentity
            ? 'outbound'
            : 'inbound',
      }
    },
    [userIdentity]
  )

  const clearTypingPing = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current)
      typingTimerRef.current = undefined
    }
  }, [])

  const fetchAccessToken = useCallback(
    async (signal?: AbortSignal): Promise<TwilioTokenResponse> => {
      if (tokenFetcherRef.current) {
        return tokenFetcherRef.current(signal)
      }

      if (!tokenEndpoint) {
        throw new Error(
          'Missing Twilio token endpoint. Provide it via VITE_TWILIO_TOKEN_ENDPOINT or useTwilioChat options.'
        )
      }

      const fetchOptions = fetchOptionsRef.current ?? {}
      const response = await fetch(tokenEndpoint, {
        method: 'GET',
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          ...(fetchOptions.headers ?? {}),
        },
        signal,
      })

      if (!response.ok) {
        throw new Error(
          `Unable to fetch Twilio token (${response.status} ${response.statusText})`
        )
      }

      const payload = (await response.json()) as TwilioTokenResponse
      if (!payload.token) {
        throw new Error('Twilio token response is missing token')
      }

      return payload
    },
    [tokenEndpoint]
  )

  const refreshToken = useCallback(async () => {
    try {
      const { token, identity } = await fetchAccessToken()
      if (identity && identity !== userIdentity) {
        setUserIdentity(identity)
      }
      if (clientRef.current) {
        await clientRef.current.updateToken(token)
        debug('Token refreshed')
      }
    } catch (refreshError) {
      debug('Token refresh failed', refreshError)
      setError('Unable to refresh Twilio token')
    }
  }, [debug, fetchAccessToken, userIdentity])

  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }
    if (!autoRefreshMs) return
    refreshTimerRef.current = setTimeout(() => {
      refreshToken()
    }, autoRefreshMs)
  }, [autoRefreshMs, refreshToken])

  const primeMessages = useCallback(
    async (activeConversation: Conversation, identity?: string | null) => {
      const paginator = await activeConversation.getMessages()
      const hydrated = paginator.items.map(item => mapMessage(item, identity))
      setMessages(sortMessages(hydrated))
    },
    [mapMessage]
  )

  const attachConversationListeners = useCallback(
    (activeConversation: Conversation, identity?: string | null) => {
      const handleMessageAdded = (message: Message) => {
        setMessages(prev =>
          sortMessages([
            ...prev.filter(existing => existing.sid !== message.sid),
            mapMessage(message, identity),
          ])
        )
      }

      const handleTypingStarted = (participant: Participant) => {
        if (participant.identity === (identity ?? userIdentity)) return
        setTypingParticipant(participant.identity ?? 'Someone')
      }

      const handleTypingEnded = (participant: Participant) => {
        if (participant.identity === (identity ?? userIdentity)) return
        setTypingParticipant(current =>
          current === (participant.identity ?? 'Someone') ? null : current
        )
      }

      const handleParticipantUpdated = () => {
        setParticipantVersion(version => version + 1)
      }

      activeConversation.on('messageAdded', handleMessageAdded)
      activeConversation.on('typingStarted', handleTypingStarted)
      activeConversation.on('typingEnded', handleTypingEnded)
      activeConversation.on('participantUpdated', handleParticipantUpdated)

      return () => {
        activeConversation.off('messageAdded', handleMessageAdded)
        activeConversation.off('typingStarted', handleTypingStarted)
        activeConversation.off('typingEnded', handleTypingEnded)
        activeConversation.off('participantUpdated', handleParticipantUpdated)
      }
    },
    [mapMessage, userIdentity]
  )

  const attachClientListeners = useCallback(
    (activeClient: Client) => {
      const handleConnectionChange = (state: string) => {
        if (state === 'connected') {
          setError(null)
        }
        if (state === 'disconnected') {
          setError('Connection lost. Attempting to reconnect…')
        }
      }

      activeClient.on('tokenAboutToExpire', refreshToken)
      activeClient.on('tokenExpired', refreshToken)
      activeClient.on('connectionStateChanged', handleConnectionChange)

      return () => {
        activeClient.off('tokenAboutToExpire', refreshToken)
        activeClient.off('tokenExpired', refreshToken)
        activeClient.off('connectionStateChanged', handleConnectionChange)
      }
    },
    [refreshToken]
  )

  useEffect(() => {
    if (!isBrowser) {
      setError('Twilio Conversations is only available in the browser.')
      setLoading(false)
      return
    }

    let isMounted = true
    let detachConversationListeners: (() => void) | null = null
    let detachClientListeners: (() => void) | null = null
    const abortController = new AbortController()

    const initialise = async () => {
      setLoading(true)
      setError(null)

      const useRestApiOnly =
        options?.useRestApiForMessages && options?.loadChatHistory

      // If using REST API only, load chat history directly without Twilio
      if (useRestApiOnly && options?.restApiConversationId) {
        try {
          debug('Loading chat history from REST API (REST API only mode)...')
          const history = await getChatHistory(options.restApiConversationId)
          const isTherapist = options.restApiSenderRole === 'therapist'
          const historyMessages = history.map(msg =>
            mapChatHistoryMessage(msg, options?.identity ?? null, isTherapist)
          )
          debug('Loaded chat history messages:', historyMessages.length)
          setMessages(sortMessages(historyMessages))
          setError(null) // Clear any errors since REST API is working
          setLoading(false)
          return // Skip Twilio initialization
        } catch (historyError) {
          debug('Failed to load chat history:', historyError)
          setError(
            historyError instanceof Error
              ? historyError.message
              : 'Failed to load chat history'
          )
          setLoading(false)
          return
        }
      }

      // Try Twilio connection (optional if REST API is available)
      try {
        const { token, conversationSid, identity } = await fetchAccessToken(
          abortController.signal
        )
        if (!isMounted || abortController.signal.aborted) return

        debug('Token fetched, conversationSid from token:', conversationSid)

        const twilioClient = await Client.create(token)
        if (!isMounted) return

        clientRef.current = twilioClient
        setClient(twilioClient)

        const derivedIdentity =
          options?.identity ??
          identity ??
          getClientIdentity(twilioClient) ??
          null
        if (derivedIdentity) {
          setUserIdentity(derivedIdentity)
        }

        detachClientListeners = attachClientListeners(twilioClient)
        scheduleTokenRefresh()

        // Prefer conversationSid from token response (most reliable), then options, then resolver
        let targetConversationSid =
          conversationSid ?? options?.conversationSid ?? options?.conversationId
        debug('Initial targetConversationSid:', targetConversationSid)

        if (!targetConversationSid && conversationResolverRef.current) {
          debug('No conversationSid found, resolving via resolver...')
          try {
            targetConversationSid = await conversationResolverRef.current(
              abortController.signal
            )
            debug('Resolved conversation SID:', targetConversationSid)
          } catch (resolverError) {
            debug('Resolver failed:', resolverError)
            // If REST API is available, don't fail - just use REST API
            if (useRestApiOnly && options?.restApiConversationId) {
              debug('Twilio failed but REST API available, loading history...')
              try {
                const history = await getChatHistory(
                  options.restApiConversationId
                )
                const isTherapist = options.restApiSenderRole === 'therapist'
                const historyMessages = history.map(msg =>
                  mapChatHistoryMessage(
                    msg,
                    derivedIdentity ?? null,
                    isTherapist
                  )
                )
                setMessages(sortMessages(historyMessages))
                setError(null) // Clear errors since REST API is working
              } catch (historyError) {
                debug('Failed to load chat history:', historyError)
              }
              setLoading(false)
              return
            }
            throw new Error(
              `Failed to create conversation: ${
                resolverError instanceof Error
                  ? resolverError.message
                  : 'Unknown error'
              }`
            )
          }
        }

        if (!targetConversationSid) {
          // If REST API is available, don't fail
          if (useRestApiOnly && options?.restApiConversationId) {
            debug(
              'No conversationSid but REST API available, loading history...'
            )
            try {
              const history = await getChatHistory(
                options.restApiConversationId
              )
              const isTherapist = options.restApiSenderRole === 'therapist'
              const historyMessages = history.map(msg =>
                mapChatHistoryMessage(msg, derivedIdentity ?? null, isTherapist)
              )
              setMessages(sortMessages(historyMessages))
              setError(null) // Clear errors since REST API is working
            } catch (historyError) {
              debug('Failed to load chat history:', historyError)
            }
            setLoading(false)
            return
          }
          throw new Error('Conversation SID is missing.')
        }

        debug('Attempting to get conversation by SID:', targetConversationSid)
        let activeConversation: Conversation | null = null
        try {
          activeConversation = await twilioClient.getConversationBySid(
            targetConversationSid
          )
        } catch (sidError) {
          debug('Failed to get conversation by SID:', sidError)

          // If REST API is available, don't fail - just use REST API
          if (useRestApiOnly && options?.restApiConversationId) {
            debug(
              'Twilio conversation not found but REST API available, loading history...'
            )
            try {
              const history = await getChatHistory(
                options.restApiConversationId
              )
              const isTherapist = options.restApiSenderRole === 'therapist'
              const historyMessages = history.map(msg =>
                mapChatHistoryMessage(msg, derivedIdentity ?? null, isTherapist)
              )
              setMessages(sortMessages(historyMessages))
              setError(null) // Clear errors since REST API is working
            } catch (historyError) {
              debug('Failed to load chat history:', historyError)
            }
            setLoading(false)
            return
          }

          // If conversation not found and we have a resolver, try creating it
          const errorMessage =
            sidError instanceof Error ? sidError.message : String(sidError)
          const isNotFound =
            errorMessage.includes('Not Found') ||
            errorMessage.includes('not found')

          if (isNotFound && conversationResolverRef.current) {
            debug(
              'Conversation not found, attempting to create via resolver...'
            )
            try {
              const newConversationSid = await conversationResolverRef.current(
                abortController.signal
              )
              debug('Created/resolved conversation SID:', newConversationSid)

              if (!newConversationSid) {
                throw new Error('Resolver returned empty conversation SID')
              }

              // Retry getting the conversation (but don't fail if REST API available)
              const maxRetries = 3
              for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                  if (attempt > 0) {
                    const delay = 500 * Math.pow(2, attempt - 1)
                    await new Promise(resolve => setTimeout(resolve, delay))
                  }
                  activeConversation =
                    await twilioClient.getConversationBySid(newConversationSid)
                  debug('Successfully retrieved conversation after creation')
                  break
                } catch (retryError) {
                  if (attempt === maxRetries - 1) {
                    // If REST API available, don't throw error
                    if (useRestApiOnly && options?.restApiConversationId) {
                      debug(
                        'Twilio failed after retries but REST API available, loading history...'
                      )
                      try {
                        const history = await getChatHistory(
                          options.restApiConversationId
                        )
                        const isTherapist =
                          options.restApiSenderRole === 'therapist'
                        const historyMessages = history.map(msg =>
                          mapChatHistoryMessage(
                            msg,
                            derivedIdentity ?? null,
                            isTherapist
                          )
                        )
                        setMessages(sortMessages(historyMessages))
                        setError(null) // Clear errors since REST API is working
                      } catch (historyError) {
                        debug('Failed to load chat history:', historyError)
                      }
                      setLoading(false)
                      return
                    }
                    throw retryError
                  }
                }
              }
            } catch (resolverError) {
              // If REST API available, don't fail
              if (useRestApiOnly && options?.restApiConversationId) {
                debug(
                  'Twilio creation failed but REST API available, loading history...'
                )
                try {
                  const history = await getChatHistory(
                    options.restApiConversationId
                  )
                  const isTherapist = options.restApiSenderRole === 'therapist'
                  const historyMessages = history.map(msg =>
                    mapChatHistoryMessage(
                      msg,
                      derivedIdentity ?? null,
                      isTherapist
                    )
                  )
                  setMessages(sortMessages(historyMessages))
                  setError(null) // Clear errors since REST API is working
                } catch (historyError) {
                  debug('Failed to load chat history:', historyError)
                }
                setLoading(false)
                return
              }
              throw resolverError
            }
          } else {
            // If REST API available, don't fail
            if (useRestApiOnly && options?.restApiConversationId) {
              debug(
                'Twilio not available but REST API available, loading history...'
              )
              try {
                const history = await getChatHistory(
                  options.restApiConversationId
                )
                const isTherapist = options.restApiSenderRole === 'therapist'
                const historyMessages = history.map(msg =>
                  mapChatHistoryMessage(
                    msg,
                    derivedIdentity ?? null,
                    isTherapist
                  )
                )
                setMessages(sortMessages(historyMessages))
                setError(null) // Clear errors since REST API is working
              } catch (historyError) {
                debug('Failed to load chat history:', historyError)
              }
              setLoading(false)
              return
            }
            throw sidError
          }
        }

        // If we have an active conversation, set it up
        if (activeConversation) {
          if (!isMounted) return
          conversationRef.current = activeConversation
          setConversation(activeConversation)

          // Load chat history from REST API if enabled
          if (options?.loadChatHistory && options?.restApiConversationId) {
            try {
              debug('Loading chat history from REST API...')
              const history = await getChatHistory(
                options.restApiConversationId
              )
              const isTherapist = options.restApiSenderRole === 'therapist'
              const historyMessages = history.map(msg =>
                mapChatHistoryMessage(msg, derivedIdentity ?? null, isTherapist)
              )
              debug('Loaded chat history messages:', historyMessages.length)

              // Merge with Twilio messages (if any) and deduplicate by SID
              try {
                const twilioMessages = await activeConversation.getMessages()
                const twilioMapped = twilioMessages.items.map(item =>
                  mapMessage(item, derivedIdentity)
                )

                // Combine and deduplicate by SID
                const allMessages = [...historyMessages, ...twilioMapped]
                const uniqueMessages = allMessages.reduce((acc, msg) => {
                  if (!acc.find(m => m.sid === msg.sid)) {
                    acc.push(msg)
                  }
                  return acc
                }, [] as MessageType[])

                setMessages(sortMessages(uniqueMessages))
              } catch (twilioError) {
                debug(
                  'Failed to get Twilio messages, using history only:',
                  twilioError
                )
                setMessages(sortMessages(historyMessages))
              }
            } catch (historyError) {
              debug(
                'Failed to load chat history, falling back to Twilio messages:',
                historyError
              )
              // Fall back to Twilio messages if history fails
              await primeMessages(activeConversation, derivedIdentity)
            }
          } else {
            // Use Twilio messages
            await primeMessages(activeConversation, derivedIdentity)
          }

          detachConversationListeners = attachConversationListeners(
            activeConversation,
            derivedIdentity
          )
        }
      } catch (initialisationError) {
        if (!isMounted) return
        debug('initialisation error', initialisationError)

        // If REST API is available, don't show error - just load history
        if (useRestApiOnly && options?.restApiConversationId) {
          try {
            debug('Twilio failed, loading chat history from REST API...')
            const history = await getChatHistory(options.restApiConversationId)
            const isTherapist = options.restApiSenderRole === 'therapist'
            const historyMessages = history.map(msg =>
              mapChatHistoryMessage(msg, options?.identity ?? null, isTherapist)
            )
            setMessages(sortMessages(historyMessages))
            setError(null) // Clear errors since REST API is working
          } catch (historyError) {
            debug('Failed to load chat history:', historyError)
            setError(
              historyError instanceof Error
                ? historyError.message
                : 'Failed to load chat history'
            )
          }
        } else {
          setError(
            initialisationError instanceof Error
              ? initialisationError.message
              : 'Unable to start chat'
          )
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    initialise()

    return () => {
      isMounted = false
      abortController.abort()
      clearTypingPing()

      if (detachConversationListeners) {
        detachConversationListeners()
      }
      if (detachClientListeners) {
        detachClientListeners()
      }

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }

      if (conversationRef.current) {
        const conversationInstance = conversationRef.current
        conversationInstance.removeAllListeners?.()
      }

      if (clientRef.current) {
        const clientInstance = clientRef.current
        clientInstance.removeAllListeners?.()
        clientInstance
          .shutdown()
          .catch(() => {
            /* noop */
          })
          .finally(() => {
            clientRef.current = null
          })
      }
    }
  }, [
    attachClientListeners,
    attachConversationListeners,
    clearTypingPing,
    debug,
    fetchAccessToken,
    options?.conversationSid,
    options?.identity,
    primeMessages,
    scheduleTokenRefresh,
  ])

  const sendMessage = useCallback(
    async (message: string) => {
      const trimmed = message.trim()
      if (!trimmed) return

      // Use REST API if configured
      if (
        options?.useRestApiForMessages &&
        options?.restApiConversationId &&
        options?.restApiSenderRole &&
        options?.restApiSenderId &&
        options?.restApiRecipientId
      ) {
        try {
          debug('Sending message via REST API...')

          if (!options.restApiRecipientId) {
            throw new Error('Recipient ID is missing')
          }

          // API expects senderId to be the recipient's MongoDB ID
          // If patient is sending: senderId = therapist's MongoDB ID
          // If therapist is sending: senderId = patient's MongoDB ID
          await sendMessageViaApi(
            {
              conversationId: options.restApiConversationId,
              senderRole: options.restApiSenderRole,
              senderId: options.restApiRecipientId, // Recipient's MongoDB ID (always a string)
              message: trimmed,
            },
            undefined
          )
          debug('Message sent via REST API')

          // Add message to local state immediately for better UX
          const newMessage: MessageType = {
            sid: `temp-${Date.now()}`,
            author: options.restApiSenderId ?? 'Me',
            body: trimmed,
            createdAt: new Date().toISOString(),
            direction: 'outbound',
            status: 'sent',
          }
          setMessages(prev => sortMessages([...prev, newMessage]))
        } catch (sendError) {
          debug('REST API send message error', sendError)
          setError('Unable to send message. Please retry.')
          throw sendError
        }
        return
      }

      // Use Twilio SDK
      const activeConversation = conversationRef.current
      if (!activeConversation) {
        throw new Error('Conversation is not ready yet.')
      }

      try {
        await activeConversation.sendMessage(trimmed)
      } catch (sendError) {
        debug('send message error', sendError)
        setError('Unable to send message. Please retry.')
        throw sendError
      }
    },
    [debug, options]
  )

  const beginTyping = useCallback(() => {
    const activeConversation = conversationRef.current
    if (!activeConversation) return

    activeConversation.typing().catch(() => {
      /* swallow typing errors */
    })

    clearTypingPing()
    typingTimerRef.current = setTimeout(() => {
      activeConversation.typing().catch(() => {
        /* swallow typing errors */
      })
    }, TYPING_PING_MS)
  }, [clearTypingPing])

  const endTyping = useCallback(() => {
    clearTypingPing()
  }, [clearTypingPing])

  return {
    client,
    conversation,
    messages,
    loading,
    error,
    sendMessage,
    beginTyping,
    endTyping,
    typingParticipant,
    participantDisplayName: options?.participantDisplayName ?? null,
    participantAvatarUrl: options?.participantAvatarUrl ?? null,
    currentUserDisplayName: options?.currentUserDisplayName ?? null,
  }
}
